import React from "react"

import { type ConfigFormValues, exampleRouteCost } from "@/lib/configForm"
import { loadFactorTone, toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { CollectionsConfig, VehicleType } from "@/types/collections"

// Keyed to a value so the swatch comes from `loadFactorTone` itself and cannot drift.
const LOAD_FACTOR_BANDS: { at: number; label: string; meaning: string }[] = [
  { at: 100, label: "≥100%", meaning: "over capacity" },
  { at: 80, label: "80–99%", meaning: "approaching capacity" },
  { at: 0, label: "<80%", meaning: "within capacity" },
]

interface CostReferenceProps {
  vehicleTypes: VehicleType[]
  config: CollectionsConfig | undefined
  /** Saved or not; the examples price against it while it is edited. */
  configForm: ConfigFormValues
  hasUnsavedChanges: boolean
}

const CostReference: React.FC<CostReferenceProps> = ({
  vehicleTypes,
  config,
  configForm,
  hasUnsavedChanges,
}) => (
  <div className="bg-white rounded-lg border border-palette-jungleGreen200 shadow-sm">
    <div className="px-6 py-4 border-b border-palette-jungleGreen200">
      <h2 className="text-lg font-semibold text-palette-jungleGreen900">
        Cost & Load Factor
      </h2>
      <p className="text-sm text-palette-jungleGreen500 mt-1">
        How route costs and load factor are calculated
      </p>
    </div>

    <div className="px-6 py-6 space-y-8">
      <section className="space-y-4">
        <h3 className="text-base font-semibold text-palette-jungleGreen900 border-b border-palette-jungleGreen100 pb-2">
          Cost calculation
        </h3>
        <div className="bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-lg p-4 text-sm">
          <p className="font-medium text-palette-jungleGreen900 mb-2">Formula</p>
          <p className="text-palette-jungleGreen700 mb-2">
            <strong>Total Route Cost</strong> = Vehicle Lease Cost + Charging Cost +
            Labour Cost
          </p>
          <ul className="list-disc list-inside space-y-1 text-palette-jungleGreen700">
            <li>
              <strong>Vehicle Lease Cost</strong> = Monthly Lease Cost ÷ 30 (daily
              prorated)
            </li>
            <li>
              <strong>Charging Cost</strong> = {configForm.internal_charging_range_pct}%
              of vehicle range at On Site rate (global); remaining miles at Off Site
              Fast rate (global). HGV: same % of mileage at On Site rate, remainder at
              Oaklands rate (global).
            </li>
            <li>
              <strong>On Site Cost per Mile</strong> = On Site Charging Cost (£/kWh,
              global) ÷ Miles per kWh (vehicle)
            </li>
            <li>
              <strong>Labour Cost</strong> = Duration (hours) × Vehicle Labour Hourly
              Rate (fallback: global driver wage rate)
            </li>
            <li>
              <strong>Planned mileage</strong> = point-to-point distance × (1 + Planned
              Mileage % Add / 100); actual from Samsara used as-is.
            </li>
          </ul>
        </div>

        {vehicleTypes.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-palette-jungleGreen700">
                Example: 350-mile route at 8 hours
              </p>
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Using unsaved values
                </span>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {vehicleTypes
                .filter((vt) => vt.is_active)
                .map((vt) => {
                  const costs = exampleRouteCost(
                    vt,
                    config,
                    configForm,
                    hasUnsavedChanges,
                    350,
                    480,
                  )
                  return (
                    <div
                      key={vt.id}
                      className="border border-palette-jungleGreen200 rounded-lg p-3 text-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-palette-jungleGreen900">
                          {vt.display_name || vt.name}
                        </span>
                        <span className="font-semibold text-palette-jungleGreen900">
                          £{costs.totalCost}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-palette-jungleGreen600">
                        <span>Lease: £{costs.dailyLeaseCost}</span>
                        <span>Charging: £{costs.chargingCost}</span>
                        <span>Labour: £{costs.driverWageCost}</span>
                      </div>
                    </div>
                  )
                })}
            </div>
            <p className="text-xs text-palette-jungleGreen500">
              Charging is £0 when a vehicle has Miles per kWh = 0 or when Global Config
              charging rates are 0. Set these in Vehicle Types and Global Config to see
              charging costs.
            </p>
          </div>
        ) : (
          <p className="text-sm text-palette-jungleGreen500">
            Add vehicle types in the Vehicle Types tab to see cost examples.
          </p>
        )}
      </section>

      <section className="space-y-4 pt-2 border-t border-palette-jungleGreen200">
        <h3 className="text-base font-semibold text-palette-jungleGreen900 border-b border-palette-jungleGreen100 pb-2">
          Load factor
        </h3>
        <p className="text-sm text-palette-jungleGreen700">
          Load factor is the percentage of a vehicle’s capacity used on a route, in{" "}
          <strong>UK pallet equivalents</strong>. All loading units are converted using
          Global Config ratios, then compared to the vehicle’s UK pallet capacity.
        </p>
        <div className="bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-lg p-4 text-sm">
          <p className="font-medium text-palette-jungleGreen900 mb-2">Formula</p>
          <p className="text-palette-jungleGreen700 mb-2">
            <strong>Load factor (%)</strong> = (Used UK pallet equivalent ÷ Vehicle UK
            pallet capacity) × 100
          </p>
          <p className="text-palette-jungleGreen700">
            <strong>Used UK pallet equivalent</strong> = for each loading unit type
            count × ratio (from Global Config) summed.
          </p>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-palette-jungleGreen900 mb-1">
              Forecast sync (where counts come from)
            </p>
            <p className="text-sm text-palette-jungleGreen700 mb-1.5">
              Forecasts are fetched from Airtable (Central Capacity Plan) and matched to
              stops by <strong>customer account</strong>, <strong>service</strong> (e.g.
              2D, SD) and <strong>date</strong> - via each stop’s location and its
              linked customer(s). When multiple stops match the same
              customer/service/date, the forecast parcel count is{" "}
              <strong>split evenly</strong> across those stops so the total still
              matches Airtable.
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-palette-jungleGreen900 mb-1">
              How “used” is worked out
            </p>
            <ul className="list-disc list-inside space-y-0.5 text-sm text-palette-jungleGreen700">
              <li>
                Volume or loading units per stop → summed by type (UK pallets, EUR
                pallets, cages, magnums, sort bags)
              </li>
              <li>Depot/internal stops are ignored</li>
            </ul>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-palette-jungleGreen900 mb-1">
            Thresholds (app colours)
          </p>
          <ul className="space-y-1 text-sm">
            {LOAD_FACTOR_BANDS.map((band) => {
              const tone = toneClasses(loadFactorTone(band.at))
              return (
                <li key={band.label} className="flex items-center gap-2">
                  <span className={cn("w-2.5 h-2.5 rounded-full shrink-0", tone.dot)} />
                  <strong className={tone.text}>{band.label}</strong> {band.meaning}
                </li>
              )
            })}
          </ul>
        </div>
        <div>
          <p className="text-sm font-medium text-palette-jungleGreen900 mb-2">
            Worked examples
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="border border-palette-jungleGreen200 rounded-lg p-3 text-sm">
              <p className="font-medium text-palette-jungleGreen900 mb-1">
                Over capacity
              </p>
              <p className="text-palette-jungleGreen700">
                26 UK pallet capacity, 20 UK + 8 cages (1 cage = 1.67) → used 33.36 →{" "}
                <strong>128%</strong>
              </p>
            </div>
            <div className="border border-palette-jungleGreen200 rounded-lg p-3 text-sm">
              <p className="font-medium text-palette-jungleGreen900 mb-1">Good</p>
              <p className="text-palette-jungleGreen700">
                26 UK pallet capacity, 10 UK + 4 sort bags (1 bag = 3.43) → used 23.72 →{" "}
                <strong>91%</strong>
              </p>
            </div>
            <div className="border border-palette-jungleGreen200 rounded-lg p-3 text-sm">
              <p className="font-medium text-palette-jungleGreen900 mb-1">Moderate</p>
              <p className="text-palette-jungleGreen700">
                26 UK pallet capacity, 6 EUR + 5 cages + 3 magnums → used 18.85 →{" "}
                <strong>73%</strong>
              </p>
            </div>
          </div>
        </div>
        <p className="text-xs text-palette-jungleGreen500">
          Load factor is shown only for routes with at least one shipper collection
          stop.
        </p>
      </section>
    </div>
  </div>
)

export default CostReference
